import React, { FunctionComponent, useRef } from 'react';
import { Query } from 'react-apollo';
import { path, pathOr, reject, isEmpty } from 'ramda';
import { match, withRouter, Link } from 'react-router-dom';
import withStyles from 'react-jss';
import { ApolloError } from 'apollo-boost';
import { compose } from 'recompose';
import classnames from 'classnames';

import Pagination from 'components/Pagination';
import { ChartPlaceholder } from 'components/Placeholder';
import ErrorBoundary from 'components/ErrorBoundary';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { getGraphQLError } from 'helpers/getGraphQLError';

import VenueTable from 'scenes/Home/components/VenueTable';
import {
  Venue,
  CardNetworkVenueStatus,
} from 'scenes/Home/components/VenueTable/VenueTable';
import { getBulkConfig, CSVFields } from 'scenes/Home/Home.helpers';
import DownloadWidget from 'components/DownloadWidget';

import { GET_MERCHANT_VENUES_RESULTS } from './VenueSearchResults.queries';
import { PageDetails } from 'components/Pagination/Pagination';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { VenueTypeValues } from 'scenes/Home/components/MerchantVenueSearch/MerchantVenueSearch.helper';

import { ClassProp } from 'helpers/types';

const styles = {
  headerText: {
    color: '#440C7F !important',
    fontWeight: 500,
    marginBottom: 0,
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  filters: {
    width: '40%',
  },
  downloadWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};

type Props = {
  searchType: string;
  searchText: string;
  cnidsToFilter: string;
  venueType: VenueTypeValues;
  filter: string;
  merchantId: string;
  match: match;
  location: Location;
  classes: ClassProp<typeof styles>;
  onCompleted?: Function;
};

type Styles = ClassProp<typeof styles>;

type Response = {
  searchMerchantVenues: {
    pagination: PageDetails;
    result: [Venue];
    __typename: string;
  };
  classes: Styles;
};

type ResponseParams = {
  data?: Response;
  loading?: boolean;
  error?: ApolloError;
  fetchMore: any;
};

const defaultErrorMessage = {
  displayMessage: 'Something went wrong',
  message: 'Not enough data to display at this time.',
};

let pageNum = 1;

const renderSearchHelpers = (classes: Styles, match: match) => (
  <div className={classes.buttonWrapper}>
    <Link to={match.url}>
      <i
        aria-hidden="true"
        className="arrow alternate circle left outline icon"
      />
      Back to All Venues
    </Link>
  </div>
);

const formatCNStatus = (status: string) => {
  if (status === CardNetworkVenueStatus.ACTIVATED_AND_TRANSACTING) {
    return 'TRANSACTING';
  }
  if (status === CardNetworkVenueStatus.NEEDS_RESOLVE) {
    return 'NOT_CONNECTED';
  }
  return status;
};

const formatCNTxnState = (status: string) => {
  return status === CardNetworkVenueStatus.ACTIVATED_AND_TRANSACTING
    ? 'Y'
    : 'N';
};

const responseMapping = (venue: Venue) => ({
  ...venue,
  transactingOnVisa: formatCNTxnState(
    pathOr('N/A', ['cardNetworkStatus', 'VISA'], venue),
  ),
  transactingOnMC: formatCNTxnState(
    pathOr('N/A', ['cardNetworkStatus', 'MASTERCARD'], venue),
  ),
  transactingOnAmex: formatCNTxnState(
    pathOr('N/A', ['cardNetworkStatus', 'AMEX'], venue),
  ),
  statusOfVisa: formatCNStatus(
    pathOr('N/A', ['cardNetworkStatus', 'VISA'], venue),
  ),
  statusOfMC: formatCNStatus(
    pathOr('N/A', ['cardNetworkStatus', 'MASTERCARD'], venue),
  ),
  statusOfAmex: formatCNStatus(
    pathOr('N/A', ['cardNetworkStatus', 'AMEX'], venue),
  ),
});

const renderVenues = (
  venues: Venue[],
  match: match,
  location: Location,
  contentRef: React.MutableRefObject<null>,
  classes: Styles,
  searchType: string,
  searchText: string,
  venueType: string,
  isDemoUser: boolean,
) => (
  <>
    {searchText && (
      <h3 className={classes.headerText}>
        Results for {searchType}, &quot;{searchText}&quot;
      </h3>
    )}
    {venueType && (
      <h4 className={classes.headerText}>
        Filtering by venueType &quot;{venueType}&quot;
      </h4>
    )}
    <div
      className={classnames({
        [classes.downloadWrapper]: searchType,
      })}
    >
      {searchType && renderSearchHelpers(classes, match)}
      {!isDemoUser && (
        <DownloadWidget
          nodeRef={contentRef}
          bulkConfig={getBulkConfig({ match, location, responseMapping })}
          imageName={`${path([0, 'venueName'], venues)}_Venues`}
          className={classnames({
            [classes.filters]: searchType,
          })}
          fields={CSVFields}
          csvName={`${path([0, 'venueName'], venues)}_venues`}
        />
      )}
    </div>
    <div ref={contentRef}>
      <VenueTable data={venues} />
    </div>
  </>
);

const loadMore = (
  fetchMore: Function,
  pagination: PageDetails,
  params: any,
) => (pageNumber: number) => {
  pageNum = pageNumber;
  return fetchMore({
    variables: {
      params: {
        ...params,
        pagingInput: {
          page: pageNumber,
          pageSize: pagination.pageSize,
        },
      },
    },
    updateQuery: (previousResult: Response, { fetchMoreResult }: any) => {
      const venuesPath = ['searchMerchantVenues', 'result'];
      const previousVenues = pathOr([], venuesPath, previousResult);
      const newVenues = pathOr([], venuesPath, fetchMoreResult);
      const pagination = path(
        ['searchMerchantVenues', 'pagination'],
        fetchMoreResult,
      );

      return newVenues.length
        ? {
            searchMerchantVenues: {
              pagination,
              result: [...previousVenues, ...newVenues],
              __typename: 'MerchantVenuesListResults',
            },
          }
        : previousResult;
    },
  });
};

const MerchantVenues: FunctionComponent<Props> = ({
  searchType,
  searchText,
  filter,
  cnidsToFilter,
  venueType,
  merchantId,
  match,
  location,
  classes,
  onCompleted,
}) => {
  const { startDate, endDate } = useDatePicker();
  const { isDemoUser } = useUserMgmt();
  const contentRef = useRef(null);
  const params: any = reject(isEmpty, {
    startDate,
    endDate,
    offerNetworkExternalVenueId: merchantId,
    pagingInput: {
      page: 1,
      pageSize: 50,
    },
    [searchType]: searchText,
    [filter]: cnidsToFilter,
    venueType,
  });

  return (
    <ErrorBoundary>
      <Query
        query={GET_MERCHANT_VENUES_RESULTS}
        variables={{
          params,
        }}
        notifyOnNetworkStatusChange
      >
        {({ data, loading, error, fetchMore }: ResponseParams) => {
          if (loading) {
            return <ChartPlaceholder />;
          }

          if (error) {
            getGraphQLError(defaultErrorMessage, true, error);
          }

          if (data) {
            const {
              searchMerchantVenues: { result, pagination },
            } = data;

            if (onCompleted) {
              onCompleted(result);
            }

            return (
              <>
                {renderVenues(
                  result,
                  match,
                  location,
                  contentRef,
                  classes,
                  searchType,
                  searchText,
                  venueType,
                  isDemoUser,
                )}

                <Pagination
                  onLoadMore={loadMore(fetchMore, pagination, params)}
                  pagination={pagination}
                  currPage={pageNum}
                />
              </>
            );
          }

          return null;
        }}
      </Query>
    </ErrorBoundary>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(MerchantVenues);
